/**
 * NITAQ — طبقة بيانات العرض (DEMO ONLY).
 *
 * منقولة حرفياً من `design/prototype/data/riyadh-districts.js` المعتمد.
 *
 * مراكز الأحياء حقيقية (الرياض / جدة). كل المؤشرات السوقية **مولّدة تجريبياً
 * بشكل حتمي** ولا تمثل بيانات سوق فعلية ولا تُستخدم في أي قرار.
 *
 * هذا الملف يعيش داخل طبقة Provider فقط. لا يُستورد من أي Component.
 */

export interface DemoDistrictRow {
  id: string;
  name: string;
  city: string;
  zone: string;
  lat: number;
  lng: number;
  /** متوسط سعر التنفيذ ر.س/م² */
  deal: number;
  /** متوسط سعر العرض ر.س/م² */
  ask: number;
  /** فجوة العرض/التنفيذ % */
  gap: number;
  fairLo: number;
  fairHi: number;
  /** النمو السنوي % */
  momentum: number;
  deals90: number;
  supply: number;
  /** متوسط مدة الإعلان بالأيام */
  dom: number;
  /** نسبة تخفيضات الأسعار % */
  cutRate: number;
  /** قوة المشتري 0–100 */
  buyerPower: number;
  liquidity: number;
  confidence: number;
  opportunity: number;
  /** ٣٦ نقطة شهرية لسعر التنفيذ */
  series: number[];
}

type Row = [name: string, lat: number, lng: number, base: number, zone: string, mBias: number];

function hash(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function prng(seed: number): () => number {
  let a = seed >>> 0;
  return function next(): number {
    a += 0x6d2b79f5;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const R = (r: () => number, lo: number, hi: number): number => lo + r() * (hi - lo);

const RIYADH: Row[] = [
  ['النرجس', 24.894786, 46.645201, 5150, 'شمال', 1.35],
  ['العارض', 24.898294, 46.602408, 4380, 'شمال', 1.4],
  ['القيروان', 24.87345, 46.555231, 3960, 'شمال', 1.3],
  ['الملقا', 24.800758, 46.597738, 7250, 'شمال', 0.95],
  ['الياسمين', 24.822886, 46.641978, 6100, 'شمال', 1.05],
  ['حطين', 24.761287, 46.600919, 7850, 'شمال', 0.8],
  ['الصحافة', 24.796422, 46.637652, 5600, 'شمال', 1.0],
  ['العقيق', 24.77361, 46.629982, 6450, 'شمال', 0.85],
  ['الندى', 24.805846, 46.682977, 4520, 'شمال', 1.1],
  ['النفل', 24.781884, 46.673299, 4980, 'شمال', 1.0],
  ['الوادي', 24.789444, 46.691234, 4310, 'شمال', 0.95],
  ['الفلاح', 24.797042, 46.70921, 3720, 'شمال', 1.15],
  ['الربيع', 24.794707, 46.660675, 5240, 'شمال', 1.0],
  ['المروج', 24.757228, 46.661972, 6300, 'وسط-شمال', 0.7],
  ['المصيف', 24.765469, 46.681529, 5150, 'وسط-شمال', 0.75],
  ['المرسلات', 24.748865, 46.6899, 5480, 'وسط-شمال', 0.7],
  ['الورود', 24.724376, 46.67849, 6720, 'وسط-شمال', 0.65],
  ['المحمدية', 24.732528, 46.648851, 8100, 'وسط-شمال', 0.6],
  ['الرحمانية', 24.71609, 46.659419, 7400, 'وسط-شمال', 0.6],
  ['الخزامى', 24.70979, 46.609486, 6850, 'غرب-شمال', 0.7],
  ['السفارات', 24.680371, 46.621552, 9100, 'غرب', 0.45],
  ['العليا', 24.692337, 46.683493, 11200, 'وسط', 0.4],
  ['الملك عبدالله', 24.732434, 46.741433, 5050, 'وسط-شرق', 0.8],
  ['قرطبة', 24.816331, 46.735797, 3480, 'شرق-شمال', 1.25],
  ['غرناطة', 24.795701, 46.753294, 3610, 'شرق-شمال', 1.15],
  ['المونسية', 24.832704, 46.767761, 2980, 'شرق-شمال', 1.45],
  ['الرمال', 24.963904, 46.759332, 2240, 'شمال-شرق', 1.75],
  ['اليرموك', 24.807833, 46.783479, 3120, 'شرق', 1.2],
  ['إشبيلية', 24.792802, 46.792184, 2870, 'شرق', 1.3],
  ['القادسية', 24.819731, 46.823963, 2380, 'شرق', 1.5],
  ['النسيم الشرقي', 24.73901, 46.845005, 1980, 'شرق', 1.05],
  ['الخليج', 24.776757, 46.802984, 2540, 'شرق', 1.1],
  ['الروضة', 24.733828, 46.767914, 3340, 'شرق', 0.85],
  ['الحمراء', 24.775512, 46.753545, 3760, 'شرق', 0.9],
  ['المغرزات', 24.764017, 46.725753, 4460, 'وسط-شمال', 0.8],
  ['النزهة', 24.756428, 46.707899, 4720, 'وسط-شمال', 0.8],
  ['الملك فهد', 24.740613, 46.670367, 7900, 'وسط-شمال', 0.55],
  ['الرائد', 24.707349, 46.638645, 6980, 'غرب-شمال', 0.6],
  ['عرقة', 24.690107, 46.587275, 5320, 'غرب', 0.9],
  ['ظهرة لبن', 24.630545, 46.530392, 2160, 'غرب', 1.35],
  ['لبن', 24.630454, 46.607244, 2640, 'غرب', 1.2],
  ['طويق', 24.568021, 46.516657, 1580, 'جنوب-غرب', 1.4],
  ['المهدية', 24.647112, 46.536471, 2320, 'غرب', 1.3],
  ['العريجاء', 24.625937, 46.656424, 2480, 'جنوب-غرب', 0.85],
  ['السويدي', 24.590777, 46.67969, 2260, 'جنوب', 0.75],
  ['الشفا', 24.565088, 46.698582, 1740, 'جنوب', 0.95],
  ['الملز', 24.664338, 46.735351, 4180, 'وسط', 0.5],
  ['بنبان', 24.984931, 46.534101, 940, 'شمال-غرب', 1.6],
];

const JEDDAH: Row[] = [
  ['أبحر الشمالية', 21.75, 39.093, 4650, 'شمال جدة', 1.2],
  ['الشراع', 21.735, 39.109, 4180, 'شمال جدة', 1.3],
  ['الياقوت', 21.79, 39.115, 3240, 'شمال جدة', 1.45],
  ['اللؤلؤ', 21.725, 39.12, 3980, 'شمال جدة', 1.15],
  ['الشاطئ', 21.653, 39.108, 5600, 'شمال جدة', 0.8],
  ['ذهبان', 21.95, 39.14, 1180, 'شمال جدة', 1.7],
  ['الحمدانية', 21.66, 39.213, 2140, 'شرق جدة', 1.0],
  ['أبرق الرغامة', 21.586, 39.278, 1620, 'شرق جدة', 1.25],
];

export const DEMO_CITIES = ['الرياض', 'جدة'] as const;

function build(rows: Row[], city: string): DemoDistrictRow[] {
  return rows.map((row) => {
    const [name, lat, lng, base, zone, mBias] = row;
    const r = prng(hash(city + name));
    const deal = Math.round((base * R(r, 0.97, 1.03)) / 5) * 5;
    const momentum = +(R(r, -1.6, 7.4) * mBias).toFixed(1);
    const gap = +(R(r, 5.5, 21) - momentum * 0.35).toFixed(1);
    const ask = Math.round((deal * (1 + gap / 100)) / 5) * 5;
    const fairLo = Math.round((deal * R(r, 0.965, 0.99)) / 5) * 5;
    const fairHi = Math.round((deal * R(r, 1.035, 1.09)) / 5) * 5;
    const deals90 = Math.round(R(r, 9, 148) * (mBias > 1.1 ? 1.15 : 0.9));
    const supply = Math.round(R(r, 60, 640));
    const dom = Math.round(R(r, 24, 118) / (mBias > 1.2 ? 1.5 : 1));
    const cutRate = +R(r, 3.4, 21).toFixed(1);
    const buyerPower = Math.round(
      Math.max(18, Math.min(88, 52 + gap * 1.6 - momentum * 3.2 + R(r, -8, 8))),
    );
    const liquidity = Math.round(Math.max(20, Math.min(96, deals90 / 1.6 + 30 - dom / 4)));
    const confidence = Math.round(
      Math.max(58, Math.min(96, 68 + deals90 / 6 - Math.abs(gap - 10) * 0.8 + R(r, -4, 6))),
    );
    const opportunity = Math.round(
      Math.max(
        38,
        Math.min(
          95,
          (100 - Math.min(60, gap * 2.4)) * 0.34 +
            (50 + momentum * 5.2) * 0.3 +
            liquidity * 0.24 +
            (100 - buyerPower * 0.4) * 0.12,
        ),
      ),
    );
    const series: number[] = [];
    let v = deal / (1 + (momentum / 100) * 3.1);
    for (let i = 0; i < 36; i++) {
      v *= 1 + momentum / 100 / 12 + (r() - 0.5) * 0.011;
      series.push(Math.round(v));
    }
    series[35] = deal;
    return {
      id: `${city}|${name}`,
      name,
      city,
      zone,
      lat,
      lng,
      deal,
      ask,
      gap,
      fairLo,
      fairHi,
      momentum,
      deals90,
      supply,
      dom,
      cutRate,
      buyerPower,
      liquidity,
      confidence,
      opportunity,
      series,
    };
  });
}

export const DEMO_DISTRICTS: DemoDistrictRow[] = [
  ...build(RIYADH, 'الرياض'),
  ...build(JEDDAH, 'جدة'),
];

export function demoDistrictById(id: string): DemoDistrictRow | undefined {
  return DEMO_DISTRICTS.find((d) => d.id === id);
}

export function demoDistrictByName(name: string, city?: string): DemoDistrictRow | undefined {
  const needle = name.trim();
  return DEMO_DISTRICTS.find((d) => d.name === needle && (!city || d.city === city));
}

/** مولّد حتمي مساعد لأي قيمة تجريبية إضافية (صفقات، تواريخ...) */
export function demoRandom(seedKey: string): () => number {
  return prng(hash(seedKey));
}
